import { Link } from "wasp/client/router";
import { cn } from "../../../client/cn";
import { getButtonPrimitiveClassName, type ButtonVariant } from "./button";
import {
  BUTTON_PRIMITIVE_DEFAULTS,
  type CanonicalButtonSize,
  type CanonicalButtonTone,
} from "./buttonPrimitiveSpec";

interface LinkButtonBaseProps {
  children: React.ReactNode;
  variant?: ButtonVariant;
  tone?: CanonicalButtonTone;
  size?: CanonicalButtonSize;
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  id?: string;
  title?: string;
  target?: string;
  rel?: string;
  ariaLabel?: string;
  tabIndex?: number;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}

type RouteLinkButtonProps = LinkButtonBaseProps & {
  to: string;
  href?: never;
};

type AnchorLinkButtonProps = LinkButtonBaseProps & {
  href: string;
  to?: never;
};

export type LinkButtonProps = RouteLinkButtonProps | AnchorLinkButtonProps;
const RouterLink = Link as unknown as React.ComponentType<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    to: string;
    children?: React.ReactNode;
  }
>;

export function LinkButton({
  variant,
  tone = BUTTON_PRIMITIVE_DEFAULTS.tone,
  size = BUTTON_PRIMITIVE_DEFAULTS.size,
  loading = BUTTON_PRIMITIVE_DEFAULTS.loading,
  disabled = false,
  fullWidth = BUTTON_PRIMITIVE_DEFAULTS.fullWidth,
  className = "",
  id,
  title,
  target,
  rel,
  ariaLabel,
  tabIndex,
  onClick,
  children,
  ...props
}: LinkButtonProps) {
  const isDisabled = Boolean(disabled || loading);
  const linkButtonClasses = cn(
    getButtonPrimitiveClassName({
      variant,
      tone,
      size,
      fullWidth,
      loading,
      className,
    }),
    isDisabled && "pointer-events-none",
  );

  const handleClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }
    onClick?.(event);
  };

  const resolvedTabIndex = isDisabled ? -1 : tabIndex;

  if ("to" in props && props.to !== undefined) {
    const { to } = props;
    return (
      <RouterLink
        to={to}
        id={id}
        title={title}
        target={target}
        rel={rel}
        className={linkButtonClasses}
        tabIndex={resolvedTabIndex}
        onClick={handleClick}
        aria-label={ariaLabel}
        aria-busy={loading || undefined}
        aria-disabled={isDisabled || undefined}
      >
        {children}
      </RouterLink>
    );
  }

  const { href } = props;
  return (
    <a
      href={href}
      id={id}
      title={title}
      target={target}
      rel={rel}
      className={linkButtonClasses}
      tabIndex={resolvedTabIndex}
      onClick={handleClick}
      aria-label={ariaLabel}
      aria-busy={loading || undefined}
      aria-disabled={isDisabled || undefined}
    >
      {children}
    </a>
  );
}
